// vCards — the one App that merged Editor + Entities (DEC-0447/0448, Gal 19.09).
// Routes are unchanged: /dashboard/editor is the instance list, /dashboard/entities
// the type catalog. What merged is the App: one name, one nav entry, one tile.
// The old names stay findable as aliases so nobody has to relearn where things went.

export const VCARDS_KEY = 'vcards';
export const VCARDS_HREF = '/dashboard/editor';

/** Old nav keys that now mean vCards — used to migrate anything stored under them. */
export const LEGACY_VCARDS_KEYS: readonly string[] = ['editor', 'entities'];

/** Names people search by. Kept in every language the UI ships. */
export const APP_ALIASES: Record<string, readonly string[]> = {
  [VCARDS_KEY]: ['עורך', 'ישויות', 'Editor', 'Entities', 'Редактор', 'Сущности'],
};

/** Map a stored key (nav key, dock id, launcher id) written before the merge to today's key. */
export function migrateAppKey(key: string): string {
  if (LEGACY_VCARDS_KEYS.includes(key)) return VCARDS_KEY;
  if (key.startsWith('page:') && LEGACY_VCARDS_KEYS.includes(key.slice(5))) return `page:${VCARDS_KEY}`;
  return key;
}

/** True when the query (already lower-cased) hits one of the app's aliases. */
export function matchesAppAlias(appKey: string, queryLower: string): boolean {
  const aliases = APP_ALIASES[appKey];
  if (!aliases || !queryLower) return false;
  return aliases.some((a) => a.toLowerCase().includes(queryLower));
}

const ENTITIES_ROOT = '/dashboard/entities';
// These two live under /dashboard/entities but belong to the Libraries folder.
const LIBRARY_SUBROUTES = ['/dashboard/entities/fields', '/dashboard/entities/field-templates'];

function under(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(root + '/');
}

/**
 * Nav highlight for an href. vCards owns both /dashboard/editor and the whole
 * /dashboard/entities subtree, so it stays lit on either — except the two library
 * sub-pages, which highlight the Libraries folder instead.
 */
export function isNavHrefActive(pathname: string, href: string): boolean {
  if (under(pathname, href)) return true;
  if (href !== VCARDS_HREF) return false;
  if (LIBRARY_SUBROUTES.some((r) => under(pathname, r))) return false;
  return under(pathname, ENTITIES_ROOT);
}

/** Migrate a stored list of app ids and drop the duplicate the merge creates. */
export function migrateAppIds(ids: readonly string[]): string[] {
  const out: string[] = [];
  for (const id of ids) {
    const next = migrateAppKey(id);
    if (!out.includes(next)) out.push(next);
  }
  return out;
}

/** Same, for an object keyed by app id (icon overrides, positions). First writer wins. */
export function migrateAppRecord<T>(rec: Record<string, T>): Record<string, T> {
  const out: Record<string, T> = {};
  for (const [k, v] of Object.entries(rec)) {
    const next = migrateAppKey(k);
    if (!(next in out) || next === k) out[next] = v;
  }
  return out;
}
